// Two-factor "trusted device" token (2026-10-20). After a code is accepted the BE answers with a
// 64-hex token (and sets the same value as an httpOnly cookie scoped to /login). The cross-site
// staging FE cannot send that cookie, so the token is kept here and replayed on the next login:
// in the POST /login/user body as `trustToken`, and as the `x-portal-2fa-trust` header on the
// magic-link status poll (a GET has no body). A live token skips the code for 7 days.
//
// localStorage, not sessionStorage, for the same iOS-PWA reason the session bearer moved
// (api/apiFetch.ts). Deliberately NOT cleared by logoutProcessor — surviving logout is the
// feature; it is dropped when a login that sent it still comes back challenged (expired, or an
// admin reset), so a dead token is not re-sent forever. One slot per device: the last person to
// pass the code on this browser is the one it remembers.

const KEY = "portal2faTrust";
const SHAPE = /^[0-9a-f]{64}$/;

export const readTrustToken = (): string | null => {
    try {
        const raw = window.localStorage.getItem(KEY);
        return raw && SHAPE.test(raw) ? raw : null;
    } catch {
        return null;
    }
};

export const storeTrustToken = (token: unknown): void => {
    if (typeof token !== "string" || !SHAPE.test(token)) return;
    try { window.localStorage.setItem(KEY, token); } catch { /* cookie path still works on same-site */ }
};

export const dropTrustToken = (): void => {
    try { window.localStorage.removeItem(KEY); } catch { /* ignore */ }
};

/** Header carrying the token on requests that have no body (the magic-link status poll). */
export const TRUST_HEADER = "x-portal-2fa-trust";
export const trustHeaders = (): Record<string, string> => {
    const token = readTrustToken();
    return token ? { [TRUST_HEADER]: token } : {};
};
